import type { AccountUpdatePayload } from '~~/app/composables/queries/useStoreApi'

export interface ProfileFormState {
  firstName: string
  lastName: string
  email: string
  birthDate: string
  /** '' = nieokreślona (id_gender 0); nie wysyłana w payloadzie */
  gender: 'male' | 'female' | ''
  newsletter: boolean
  currentPassword: string
  newPassword: string
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Zwraca datę urodzenia tylko gdy to realne YYYY-MM-DD.
 * PrestaShop oddaje brak daty jako '0000-00-00' (nie NULL), a <input type="date"> nie potrafi
 * jej wyrenderować — reactive zachowuje wtedy oryginalny string. Bez tego strażnika '0000-00-00'
 * trafiłoby z powrotem do backendu, gdzie createFromFormat('Y-m-d', '0000-00-00') daje '-0001-11-30'
 * i Customer odrzuca zapis ("Property Customer->birthday is not valid").
 */
export function normalizeBirthday(value: string): string | undefined {
  const trimmed = value.trim()
  if (!ISO_DATE.test(trimmed) || trimmed === '0000-00-00') {
    return undefined
  }
  return trimmed
}

export interface BirthdayValidation {
  /** Poprawna data RRRR-MM-DD gotowa do wysyłki; undefined gdy pole puste lub błędne. */
  value?: string
  /** Komunikat dla użytkownika, gdy wpisana data jest niepoprawna. */
  error?: string
}

/**
 * Waliduje datę urodzenia rozróżniając 3 stany:
 *  - pusta / '0000-00-00' (sentinel „brak daty" z PrestaShop) → {} (pole opcjonalne, bez błędu),
 *  - poprawna realna data kalendarzowa w zakresie 1900..dziś → { value },
 *  - cokolwiek innego (zły format, nieistniejąca data, rok < 1900, data z przyszłości) → { error }.
 *
 * Dzięki temu błędna data NIE jest już po cichu pomijana w payloadzie (poprzednie zachowanie
 * normalizeBirthday) — użytkownik dostaje czytelny komunikat zamiast „udanego" zapisu bez daty.
 */
export function validateBirthday(value: string): BirthdayValidation {
  const trimmed = value.trim()
  if (trimmed === '' || trimmed === '0000-00-00') {
    return {}
  }

  const normalized = normalizeBirthday(trimmed)
  if (!normalized) {
    return { error: 'Podaj poprawną datę urodzenia w formacie RRRR-MM-DD.' }
  }

  const [year, month, day] = normalized.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))
  const isRealDate
    = date.getUTCFullYear() === year
      && date.getUTCMonth() === month - 1
      && date.getUTCDate() === day
  const isFuture = date.getTime() > Date.now()

  if (!isRealDate || year < 1900 || isFuture) {
    return { error: 'Podaj poprawną datę urodzenia (między 1900 r. a dniem dzisiejszym).' }
  }

  return { value: normalized }
}

// Pola profilu — bez hasła i bez e-maila (zmiana nie-wrażliwa).
export function buildProfilePayload(form: ProfileFormState): AccountUpdatePayload {
  const payload: AccountUpdatePayload = {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    newsletter: form.newsletter,
  }
  // Wysyłaj płeć tylko po jawnym wyborze — inaczej zapis samego imienia po cichu nadpisałby
  // id_gender=0 (nieokreślona, np. konta social login) na 2 (kobieta).
  if (form.gender === 'male') {
    payload.gender = 1
  }
  else if (form.gender === 'female') {
    payload.gender = 2
  }
  const birthday = normalizeBirthday(form.birthDate)
  if (birthday) {
    payload.birthday = birthday
  }
  return payload
}

// Pola wrażliwe — e-mail i/lub nowe hasło + aktualne hasło. Bez pól profilu.
export function buildCredentialsPayload(form: ProfileFormState): AccountUpdatePayload {
  return {
    email: form.email.trim(),
    password: form.currentPassword,
    new_password: form.newPassword.trim() || undefined,
  }
}
